// A draggable support button; Intercom still owns the messenger and unread count.
import {
    hide,
    onHide,
    onShow,
    onUnreadCountChange,
    show,
} from '@intercom/messenger-js-sdk';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { IntercomMessengerIcon } from '@apps/web/components/shared/intercom_messenger_icon';
import { Button } from '@apps/web/components/ui/button';
import { useDraggableLauncherPosition } from '@apps/web/hooks/use_draggable_launcher_position';
import { cn } from '@apps/web/lib/utils';

export function IntercomLauncher({
    bottomPaddingPx,
}: {
    readonly bottomPaddingPx: number;
}) {
    const drag = useDraggableLauncherPosition();
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        // The SDK has no unsubscribe API. Ignore callbacks from a launcher
        // belonging to an earlier signed-in user or an unmounted provider.
        let active = true;
        onShow(() => {
            if (active) {
                setIsOpen(true);
            }
        });
        onHide(() => {
            if (active) {
                setIsOpen(false);
            }
        });
        onUnreadCountChange((count: number) => {
            if (active) {
                setUnreadCount(count);
            }
        });
        return () => {
            active = false;
        };
    }, []);

    return (
        <Button
            type="button"
            data-testid="intercom-launcher"
            aria-label={
                isOpen
                    ? 'Close support chat'
                    : unreadCount > 0
                      ? `Open support chat (${unreadCount} unread messages)`
                      : 'Open support chat'
            }
            aria-expanded={isOpen}
            title="Support · Drag to move"
            className={cn(
                'fixed z-[2147482999] size-12 touch-none rounded-full bg-white p-0 text-neutral-950 shadow-lg transition-none select-none hover:bg-white dark:bg-white dark:text-neutral-950 dark:hover:bg-white',
                drag.isDragging ? 'cursor-grabbing' : 'cursor-grab',
            )}
            style={
                drag.position === null
                    ? {
                          left: 'var(--intercom-left, 20px)',
                          top: 'auto',
                          bottom: `max(${bottomPaddingPx}px, var(--safe-bottom, 0px))`,
                      }
                    : {
                          left: drag.position.left,
                          top: drag.position.top,
                          bottom: 'auto',
                      }
            }
            onPointerDown={drag.onPointerDown}
            onPointerMove={drag.onPointerMove}
            onPointerUp={drag.onPointerUp}
            onPointerCancel={drag.onPointerCancel}
            onClick={() => {
                if (drag.consumeDragClick()) {
                    return;
                }
                if (isOpen) {
                    hide();
                } else {
                    show();
                }
            }}
        >
            {isOpen ? (
                <X className="size-6" aria-hidden="true" />
            ) : (
                <IntercomMessengerIcon />
            )}
            {!isOpen && unreadCount > 0 && (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-semibold text-white"
                >
                    {unreadCount > 99 ? '99+' : unreadCount}
                </span>
            )}
        </Button>
    );
}
